<template>
  <div class="flex flex-col md:items-center">
    <div class="flex flex-col bg-white lg:box-content md:max-w-[1720px] w-full" data-test="specification">
      <ProductDetailPageTechnicalSection
        :is-open="isTechnicalSectionOpen"
        :the-attributes="allAttributes.technicalAttributes"
        :brand-name="brandName"
        @update:open="(value) => (isTechnicalSectionOpen = value)"
      />

      <ProductDetailPageDescriptionSection
        v-if="isDescriptionShowing"
        :is-open="isDescriptionSectionOpen"
        :text="product.text"
        :technical-description="product.technicalDescription"
        :description="allAttributes.productDescription"
        :ean="variant.ean"
        @update:open="(value) => (isDescriptionSectionOpen = value)"
      />

      <ProductDetailPageDocumentsSection
        :is-open="isDocumentSectionOpen"
        :documents="documents"
        @update:open="(value) => (isDocumentSectionOpen = value)"
      />

      <ProductDetailPageClimateSection
        v-if="isDahl"
        :is-open="isClimateSectionOpen"
        :climate-r-s-k="product.requestedVariant?.climateRSK"
        :documents="documents"
        @update:open="(value) => (isClimateSectionOpen = value)"
      />

      <ProductDetailPageWarningsSection
        v-if="isWarningsShowing"
        ref="chemicalWarningsRef"
        :is-open="isChemicalWarningsSectionOpen"
        :warnings="warnings"
        @update:open="(value) => (isChemicalWarningsSectionOpen = value)"
      />

      <ProductDetailPageClassificationsSection
        v-if="isClassificationsShowing"
        :is-open="isClassificationSectionOpen"
        :classifications="variant.classifications"
        @update:open="(value) => (isClassificationSectionOpen = value)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { VNodeRef } from 'vue-demi';
import { storeToRefs } from 'pinia';
import { Product, Variant } from '@/types/products';
import { SUPPLIER_ITEM_NUMBER_ATTRIBUTE_NAME } from '~/utils/Products';
import { useProductStore } from '~/stores';

type Props = {
  product?: Product;
  variant?: Variant;
  attributesToHide?: string[];
};

const props = withDefaults(defineProps<Props>(), {
  product: () => ({}),
  variant: () => ({}),
  attributesToHide: () => [],
});

const emit = defineEmits<{
  (e: 'show-warnings', func: Function): void;
}>();

const { sortSequence } = storeToRefs(useProductStore());
const config = useRuntimeConfig();

const { t } = useI18n();
const { mq } = useMediaQueries();
const chemicalWarningsRef = ref<null | VNodeRef>(null);
const isClimateSectionOpen = ref(false);
const isTechnicalSectionOpen = ref(false);
const isDescriptionSectionOpen = ref(false);
const isDocumentSectionOpen = ref(false);
const isChemicalWarningsSectionOpen = ref(false);
const isClassificationSectionOpen = ref(false);
const isDahl = computed(() => config.public.brand === 'dahl');

const documents = computed(() => {
  if (!props.variant?.assets) {
    return [];
  }
  const documents = [];
  for (const asset of props.variant.assets) {
    if (!asset.assetUrl && !(asset.assetLabel || asset.assetType)) {
      continue;
    }

    let icon = 'filter_none';
    if (asset.assetMimeType === 'application/pdf' || asset.assetUrl.endsWith('.pdf')) {
      icon = 'picture_as_pdf';
    }

    const document = { ...asset, icon };
    documents.push(document);
  }

  return documents;
});

const warnings = computed(() => props.variant.warnings);
const brandName = computed(() => props.variant.brands?.[0]?.brand);
const allAttributes = computed(() => {
  const variantAttributes = props.variant.attributes || [];
  const commonAttributes = props.product.commonVariantAttributes || [];
  const allAttributes = variantAttributes.concat(commonAttributes);

  const productDescAttributes = [
    SUPPLIER_ITEM_NUMBER_ATTRIBUTE_NAME,
    t('supplier_art_nr'),
    t('synonyms'),
    t('reach_info_requirement'),
    t('svhc_names'),
    t('covered_by_rohs_directive'),
  ];
  const productDescription = allAttributes.filter((attribute) =>
    productDescAttributes.includes(attribute.attributeName),
  );

  const hiddenTechnicalAttributes = productDescAttributes.concat(props.attributesToHide);
  let technicalAttributes = allAttributes.filter(
    ({ attributeName }) => !hiddenTechnicalAttributes.includes(attributeName),
  );

  // Use sequencing to sort technical attributes if a sequence exists
  if (sortSequence.value?.length > 0) {
    // The article number attribute must be first always
    let articleNumberAttribute;
    if (technicalAttributes[0]?.attributeName === 'articleNumber') {
      articleNumberAttribute = technicalAttributes.shift();
    }

    technicalAttributes = sortAttributesWithSequencing(technicalAttributes, sortSequence.value, 'attributeName');

    if (articleNumberAttribute) {
      technicalAttributes.unshift(articleNumberAttribute);
    }
  } else {
    technicalAttributes.sort((a, b) => a.sort! - b.sort!);
  }

  return {
    productDescription,
    technicalAttributes,
  };
});
const isDescriptionShowing = computed(
  () =>
    !!(
      props.product.text ||
      props.variant.description ||
      allAttributes.value.productDescription.length ||
      props.variant.ean
    ),
);
const isClassificationsShowing = computed(
  () => !!(props.variant.classifications && props.variant.classifications.length),
);
const isWarningsShowing = computed(() => !!(warnings.value && warnings.value.length));

const showWarnings = () => {
  isChemicalWarningsSectionOpen.value = true;
  chemicalWarningsRef.value?.$el?.scrollIntoView({ behavior: 'smooth' });
};

onMounted(() => {
  // Technical Section is Open default on Desktop, mounted is the first time current has any value
  isTechnicalSectionOpen.value = !['xsmall', 'small'].includes(mq.value?.current || '');
  emit('show-warnings', showWarnings);
});
</script>

<script lang="ts">
export default defineComponent({
  name: 'ProductDetailPageSpecification',
});
</script>
